import { Calendar, ChevronDown, Edit, Info, Landmark, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
	AllTransactionViewFilterInput,
	AllTransactionViewNode,
} from "@/api/models/allTransactionView";
import type { TransactionCategoryApi } from "@/api/models/transactionCategory";
import {
	type AllTransactionViewSortInput,
	getAllTransactionDistinctValues,
	getAllTransactionsCount,
	getAllTransactionsGraphql,
} from "@/api/services/allTransactionGraphqlService";
import { updateBankTransactionCategory } from "@/api/services/bankTransactionService";
import { updatePurchaseTransactionCategory } from "@/api/services/purchaseTransactionService";
import { updateSalesTransactionCategory } from "@/api/services/salesTransactionService";
import { getAllTransactionCategories } from "@/api/services/transactionCategoryService";
import { FeatureFeedback } from "@/components/FeatureFeedback";
import {
	type ColumnConfig,
	type ColumnFilters,
	MaximusTableReusable,
} from "@/components/MaximusTableReusable";
import { useToast } from "@/hooks/use-toast";
import { useExportToExcel } from "@/hooks/useExportToExcel";
import { usePermissions } from "@/hooks/usePermissions";
import { useScreenColumnPreferences, type DefaultColumn } from "@/hooks/useScreenColumnPreferences";
import { SCREEN_NAMES } from "@/constants/screenNames";
import { cn } from "@/lib/utils";
import { useApp } from "../../contexts/AppContext";
import { DEMO_TRANSACTIONS } from "../../data/demo-data";
import { useEntityFilter } from "../../hooks/useEntityFilter";
import { useFormatting } from "../../hooks/useFormatting";
import { formatDate } from "../../lib/utils";
import { TransactionDetailModal } from "../modals/TransactionDetailModal";
import { PermissionGate } from "../permissions/PermissionGate";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

type FilterType = "all" | "overdue" | "thisWeek" | "uncategorized";

interface TransactionDisplay {
	id: string | number;
	originalId?: number;
	date: Date;
	dueDate?: Date;
	description: string;
	category: string;
	status: string;
	grossValueIn: number;
	grossValueOut: number;
	amount: number;
	source?: string;
	reference?: string;
	accountName?: string;
	type?: "income" | "expense";
	transactionType?: string;
	outstanding?: number;
	dataSourceName?: string;
}

interface TransactionsPageProps {
	accountFilter?: string;
	initialFilter?: string;
	columnFilters?: Record<string, string>;
}

const SORT_FIELD_MAP: Record<string, string> = {
	source: "Source",
	dataSourceName: "DataSourceName",
	reference: "Reference",
	accountName: "AccountName",
	description: "Description",
	transactionType: "TransactionType",
	category: "Category",
	date: "TransactionDate",
	dueDate: "DueDate",
	amount: "Amount",
	outstanding: "Outstanding",
	status: "Status",
};

const EXPORT_COLUMN_MAP: Record<string, { name: string; displayName: string }> = {
	source: { name: "source", displayName: "Source" },
	dataSourceName: { name: "datasourcename", displayName: "Data Source" },
	reference: { name: "reference", displayName: "Reference" },
	accountName: { name: "accountname", displayName: "Account Name" },
	description: { name: "description", displayName: "Description" },
	transactionType: { name: "transactiontype", displayName: "Transaction Type" },
	category: { name: "category", displayName: "Category" },
	date: { name: "transactiondate", displayName: "Transaction Date" },
	dueDate: { name: "duedate", displayName: "Due Date" },
	amount: { name: "amount", displayName: "Amount" },
	outstanding: { name: "outstanding", displayName: "Outstanding" },
	status: { name: "status", displayName: "Status" },
};

const FILTER_FIELD_MAP: Record<string, string> = {
	source: "source",
	dataSourceName: "dataSourceName",
	reference: "reference",
	accountName: "accountName",
	description: "description",
	transactionType: "transactionType",
	category: "category",
	date: "transactionDate",
	dueDate: "dueDate",
	amount: "amount",
	outstanding: "outstanding",
	status: "status",
};

const TEXT_FILTER_FIELDS = new Set([
	"source",
	"dataSourceName",
	"reference",
	"accountName",
	"description",
	"transactionType",
	"category",
	"status",
]);

const DATE_FILTER_FIELDS = new Set(["transactionDate", "dueDate"]);

const NUMBER_FILTER_FIELDS = new Set(["amount", "outstanding"]);

function buildApiFilter(
	columnFilters: ColumnFilters,
): AllTransactionViewFilterInput | undefined {
	const entries = Object.entries(columnFilters);
	if (entries.length === 0) return undefined;

	const filterInput: AllTransactionViewFilterInput = {};

	for (const [columnKey, filterValue] of entries) {
		const apiField = FILTER_FIELD_MAP[columnKey];
		if (!apiField) continue;

		if (TEXT_FILTER_FIELDS.has(apiField)) {
			if (filterValue.mode === "isEmpty") {
				(filterInput as Record<string, unknown>)[apiField] = {
					isBlank: true,
				};
			} else if (filterValue.value != null && filterValue.value !== "") {
				const strValue = String(filterValue.value);
				if (filterValue.mode === "equals") {
					(filterInput as Record<string, unknown>)[apiField] = {
						eq: strValue,
					};
				} else {
					(filterInput as Record<string, unknown>)[apiField] = {
						contains: strValue,
					};
				}
			}
		} else if (DATE_FILTER_FIELDS.has(apiField)) {
			const dateFilter: Record<string, string> = {};
			if (filterValue.dateFrom) {
				dateFilter.ge = filterValue.dateFrom.toISOString().split("T")[0];
			}
			if (filterValue.dateTo) {
				dateFilter.le = filterValue.dateTo.toISOString().split("T")[0];
			}
			if (Object.keys(dateFilter).length > 0) {
				(filterInput as Record<string, unknown>)[apiField] = dateFilter;
			}
		} else if (NUMBER_FILTER_FIELDS.has(apiField)) {
			const numFilter: Record<string, number> = {};
			if (filterValue.numberFrom != null) {
				numFilter.ge = filterValue.numberFrom;
			}
			if (filterValue.numberTo != null) {
				numFilter.le = filterValue.numberTo;
			}
			if (Object.keys(numFilter).length > 0) {
				(filterInput as Record<string, unknown>)[apiField] = numFilter;
			}
		}
	}

	return Object.keys(filterInput).length > 0 ? filterInput : undefined;
}

const DEFAULT_TRANSACTION_COLUMNS: DefaultColumn[] = [
	{ key: "source", defaultVisible: true, defaultOrder: 0 },
	{ key: "dataSourceName", defaultVisible: true, defaultOrder: 1 },
	{ key: "reference", defaultVisible: true, defaultOrder: 2 },
	{ key: "accountName", defaultVisible: true, defaultOrder: 3 },
	{ key: "description", defaultVisible: true, defaultOrder: 4 },
	{ key: "transactionType", defaultVisible: true, defaultOrder: 5 },
	{ key: "category", defaultVisible: true, defaultOrder: 6 },
	{ key: "date", defaultVisible: true, defaultOrder: 7 },
	{ key: "dueDate", defaultVisible: true, defaultOrder: 8 },
	{ key: "amount", defaultVisible: true, defaultOrder: 9 },
	{ key: "outstanding", defaultVisible: true, defaultOrder: 10 },
	{ key: "status", defaultVisible: true, defaultOrder: 11 },
	{ key: "id", defaultVisible: true, defaultOrder: 12 },
];

export const TransactionsPage: React.FC<TransactionsPageProps> = ({
	accountFilter,
	initialFilter,
	columnFilters,
}) => {
	const { auditMode, mode } = useApp();
	const { hasPermission } = usePermissions();
	const { filterByEntity } = useEntityFilter();
	const { formatCurrency } = useFormatting();
	const { toast } = useToast();
	const { visibleColumns: savedVisibleColumns, savePreferences: saveColumnPreferences } = useScreenColumnPreferences(
		SCREEN_NAMES.SYNCED_TRANSACTIONS,
		DEFAULT_TRANSACTION_COLUMNS,
	);
	const [filter, setFilter] = useState<FilterType>(
		(initialFilter as FilterType) ?? "all",
	);
	const [selectedTransaction, setSelectedTransaction] =
		useState<TransactionDisplay | null>(null);
	const [showTransactionModal, setShowTransactionModal] = useState(false);
	const [liveTransactions, setLiveTransactions] = useState<
		TransactionDisplay[]
	>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [validCategories, setValidCategories] = useState<
		TransactionCategoryApi[]
	>([]);
	const [activeAccountFilter, setActiveAccountFilter] = useState<
		string | undefined
	>(accountFilter);
	const [bannerExpanded, setBannerExpanded] = useState(false);
	const [sortParams, setSortParams] = useState<
		AllTransactionViewSortInput[] | undefined
	>(undefined);
	// Build initial API column filter from navigation prop
	const initialColumnFilterParams = useMemo((): AllTransactionViewFilterInput | undefined => {
		if (!columnFilters || Object.keys(columnFilters).length === 0) return undefined;
		const apiFilter: AllTransactionViewFilterInput = {};
		for (const [field, value] of Object.entries(columnFilters)) {
			const apiField = FILTER_FIELD_MAP[field];
			if (apiField) {
				(apiFilter as Record<string, unknown>)[apiField] = { eq: value };
			}
		}
		return Object.keys(apiFilter).length > 0 ? apiFilter : undefined;
	}, [columnFilters]);

	// Build ColumnFilters for MaximusTableReusable visual state from navigation prop
	const tableInitialColumnFilters = useMemo((): ColumnFilters | undefined => {
		if (!columnFilters || Object.keys(columnFilters).length === 0) return undefined;
		const filters: ColumnFilters = {};
		for (const [field, value] of Object.entries(columnFilters)) {
			filters[field] = { value, mode: "equals" };
		}
		return filters;
	}, [columnFilters]);

	const [columnFilterParams, setColumnFilterParams] = useState<
		AllTransactionViewFilterInput | undefined
	>(initialColumnFilterParams);

	// Server-side search state (live mode only)
	const [serverSearchTerm, setServerSearchTerm] = useState<string>("");

	// Server-side pagination state (live mode only)
	const [serverPage, setServerPage] = useState(1);
	const [serverPageSize, setServerPageSize] = useState(25);
	const [pageCursors, setPageCursors] = useState<Record<number, string>>({});

	// Server-side filter counts (live mode only)
	const [serverFilterCounts, setServerFilterCounts] = useState<{
		all: number;
		thisWeek: number;
		uncategorized: number;
	}>({ all: 0, thisWeek: 0, uncategorized: 0 });

	// Server-side dropdown options (live mode only)
	const [sourceOptions, setSourceOptions] = useState<string[]>([]);
	const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>([]);

	// Sync filter state when navigating with an initialFilter (e.g. from dashboard card)
	useEffect(() => {
		if (initialFilter) {
			setFilter(initialFilter as FilterType);
			if (mode === "live") {
				setServerPage(1);
				setPageCursors({});
			}
		}
	}, [initialFilter]);


	// ── API calls ────────────────────────────────────────────────────────────
	const buildQuickFilter = useCallback(
		(filterType: FilterType): AllTransactionViewFilterInput | undefined => {
			if (filterType === "thisWeek") {
				return { transactionDate: { lastXDays: 7 } };
			}
			if (filterType === "uncategorized") {
				return { category: { eq: "Uncategorised" } };
			}
			if (filterType === "overdue") {
				return { status: { eq: "Overdue" } };
			}
			return undefined;
		},
		[],
	);
	const fetchTransactions = async (
		sort?: AllTransactionViewSortInput[],
		first?: number,
		after?: string,
		quickFilter?: AllTransactionViewFilterInput,
		colFilter?: AllTransactionViewFilterInput,
	) => {
		setIsLoading(true);
		setError(null);
		try {
			const mergedFilter: AllTransactionViewFilterInput | undefined =
				quickFilter || colFilter ? { ...quickFilter, ...colFilter } : undefined;
			const result = await getAllTransactionsGraphql(
				mergedFilter,
				first ?? serverPageSize,
				after,
				sort,
			);
			const mapped = result.edges.map((edge) => {
				const t: AllTransactionViewNode = edge.node;
				const amt = t.amount ?? 0;
				return {
					id: `${t.source}-${t.id}-${edge.cursor}`,
					originalId: t.id,
					date: t.transactionDate ? new Date(t.transactionDate) : new Date(),
					dueDate: t.dueDate ? new Date(t.dueDate) : undefined,
					description: t.description || "",
					category: t.category || "Uncategorised",
					status: t.status || "Unknown",
					amount: amt,
					grossValueIn: amt > 0 ? amt : 0,
					grossValueOut: amt < 0 ? Math.abs(amt) : 0,
					source: t.source,
					reference: t.reference ?? undefined,
					accountName: t.accountName ?? undefined,
					type: (amt >= 0 ? "income" : "expense") as "income" | "expense",
					transactionType: t.transactionType ?? undefined,
					outstanding: t.outstanding ?? undefined,
					dataSourceName: t.dataSourceName ?? undefined,
				};
			});
			setLiveTransactions(mapped);
			return result.pageInfo;
		} catch (err) {
			console.error("Failed to fetch transactions:", err);
			setError("Failed to load transactions");
			return null;
		} finally {
			setIsLoading(false);
		}
	};

	const fetchFilterCounts = async (
		colFilter?: AllTransactionViewFilterInput,
	) => {
		try {
			const [allCount, thisWeekCount, uncategorizedCount] = await Promise.all([
				getAllTransactionsCount(colFilter),
				getAllTransactionsCount({
					transactionDate: { lastXDays: 7 },
					...colFilter,
				}),
				getAllTransactionsCount({
					category: { eq: "Uncategorised" },
					...colFilter,
				}),
			]);
			setServerFilterCounts({
				all: allCount,
				thisWeek: thisWeekCount,
				uncategorized: uncategorizedCount,
			});
		} catch (err) {
			console.error("Failed to fetch filter counts:", err);
		}
	};

	const fetchCategories = async () => {
		try {
			const response = await getAllTransactionCategories();
			setValidCategories(response.categories);
		} catch (err) {
			console.error("Failed to fetch categories:", err);
		}
	};

	const fetchFilterOptions = async () => {
		try {
			const [sources, categories, statuses] = await Promise.all([
				getAllTransactionDistinctValues("Source"),
				getAllTransactionDistinctValues("Category"),
				getAllTransactionDistinctValues("Status"),
			]);
			setSourceOptions(sources);
			setCategoryOptions(categories);
			setStatusOptions(statuses);
		} catch (err) {
			console.error("Failed to fetch filter options:", err);
		}
	};

	useEffect(() => {
		if (mode !== "live") return;
		const quickFilter = buildQuickFilter(filter);
		const searchFilter: AllTransactionViewFilterInput | undefined =
			serverSearchTerm.trim()
				? { summary: serverSearchTerm.trim() }
				: undefined;
		const cursor = serverPage === 1 ? undefined : pageCursors[serverPage];
		const currentFetchPage = serverPage;
		fetchTransactions(
			sortParams,
			serverPageSize,
			cursor,
			{ ...quickFilter, ...searchFilter },
			columnFilterParams,
		).then((pageInfo) => {
			if (pageInfo?.endCursor) {
				setPageCursors((prev) => ({
					...prev,
					[currentFetchPage + 1]: pageInfo.endCursor as string,
				}));
			}
		});
		fetchCategories();
	}, [
		mode,
		sortParams,
		serverPage,
		serverPageSize,
		filter,
		columnFilterParams,
		serverSearchTerm,
	]);

	useEffect(() => {
		if (mode === "live") {
			const searchFilter: AllTransactionViewFilterInput | undefined =
				serverSearchTerm.trim()
					? { summary: serverSearchTerm.trim() }
					: undefined;
			fetchFilterCounts({ ...columnFilterParams, ...searchFilter });
		}
	}, [mode, columnFilterParams, serverSearchTerm]);

	useEffect(() => {
		if (mode === "live") {
			fetchFilterOptions();
		}
	}, [mode]);

	const handleSearchChange = useCallback((query: string) => {
		setServerSearchTerm(query);
		setServerPage(1);
		setPageCursors({});
	}, []);

	const handleColumnFilterChange = useCallback((filters: ColumnFilters) => {
		const apiFilter = buildApiFilter(filters);
		setColumnFilterParams(apiFilter);
		setServerPage(1);
		setPageCursors({});
	}, []);

	const getAllExportColumns = useCallback(() => {
		return Object.values(EXPORT_COLUMN_MAP).map((col) => ({
			name: col.name,
			displayName: col.displayName,
		}));
	}, []);

	const getExportFilter = useCallback(() => {
		if (!columnFilterParams) return undefined;
		return columnFilterParams as Record<string, unknown>;
	}, [columnFilterParams]);

	const { triggerExport } = useExportToExcel({
		form: "SyncedTransactions",
		getColumns: getAllExportColumns,
		getFilter: getExportFilter,
		fileName: "synced-transactions-export",
		onFinished: () =>
			toast({
				title: "Export complete",
				description: "Your file has been downloaded.",
			}),
		onError: (msg) =>
			toast({
				title: "Export failed",
				description: msg,
				variant: "destructive",
			}),
	});

	const handleSortChange = useCallback(
		(column: string | null, direction: "asc" | "desc" | null) => {
			if (!column || !direction) {
				setSortParams(undefined);
			} else {
				const field = SORT_FIELD_MAP[column];
				if (!field) {
					setSortParams(undefined);
				} else {
					setSortParams([
						{
							field,
							order: direction === "asc" ? "Ascending" : "Descending",
						},
					]);
				}
			}
			setServerPage(1);
			setPageCursors({});
		},
		[],
	);

	const handleCategorySave = async (
		transaction: TransactionDisplay,
		newCategory: string,
	) => {
		if (mode !== "live" || !transaction.source) {
			console.log("Demo mode - category change not persisted:", {
				transaction,
				newCategory,
			});
			return;
		}
		if (!newCategory || newCategory.trim() === "") {
			setError("Category cannot be empty");
			return;
		}
		const isValidCategory = validCategories.some(
			(cat) => cat.name === newCategory,
		);
		if (!isValidCategory) {
			setError(
				`Invalid category: "${newCategory}". Please select a valid category.`,
			);
			return;
		}
		try {
			const numericId = transaction.originalId;
			if (numericId === undefined || numericId === null) {
				setError("Cannot update category: transaction ID is missing");
				return;
			}
			switch (transaction.source) {
				case "Sales Ledger":
					await updateSalesTransactionCategory(numericId, newCategory);
					break;
				case "Purchase Ledger":
					await updatePurchaseTransactionCategory(numericId, newCategory);
					break;
				case "Bank":
					await updateBankTransactionCategory(numericId, newCategory);
					break;
				default:
					console.error("Unknown transaction source:", transaction.source);
					return;
			}
			const qf = buildQuickFilter(filter);
			const searchFilter: AllTransactionViewFilterInput | undefined =
				serverSearchTerm.trim()
					? { summary: serverSearchTerm.trim() }
					: undefined;
			const cur = serverPage === 1 ? undefined : pageCursors[serverPage];
			await fetchTransactions(sortParams, serverPageSize, cur, { ...qf, ...searchFilter }, columnFilterParams);
			fetchFilterCounts({ ...columnFilterParams, ...searchFilter });
			setError(null);
			toast({
				title: "Category Updated",
				description: `Category changed to "${newCategory}".`,
			});
		} catch (err) {
			console.error("Failed to update category:", err);
			setError("Failed to update category");
		}
	};

	// ── Data assembly ────────────────────────────────────────────────────────
	const demoTransactions = [...DEMO_TRANSACTIONS];

	const allTransactions: TransactionDisplay[] =
		mode === "live"
			? liveTransactions
			: filterByEntity(demoTransactions).map((t) => ({
					id: t.id,
					date: t.date,
					dueDate: t.dueDate,
					description: t.description,
					category: t.category,
					status: t.status,
					amount: t.amount,
					grossValueIn: t.amount > 0 ? t.amount : 0,
					grossValueOut: t.amount < 0 ? Math.abs(t.amount) : 0,
					type: (t.amount >= 0 ? "income" : "expense") as "income" | "expense",
					source: t.source,
					reference: t.reference,
					accountName: t.accountName,
					transactionType: t.transactionType,
					outstanding: t.outstanding,
					dataSourceName: t.dataSourceName,
				}));

	const entityFilteredTransactions = allTransactions;

	// ── Quick filter counts ──────────────────────────────────────────────────
	// Derive server-side total count from filter counts
	const serverTotalCount =
		filter === "thisWeek"
			? serverFilterCounts.thisWeek
			: filter === "uncategorized"
				? serverFilterCounts.uncategorized
				: serverFilterCounts.all;

	const getFilterCount = (filterType: FilterType) => {
		if (mode === "live") {
			if (filterType === "all") return serverFilterCounts.all;
			if (filterType === "thisWeek") return serverFilterCounts.thisWeek;
			if (filterType === "uncategorized")
				return serverFilterCounts.uncategorized;
			return 0;
		}
		if (filterType === "all") return entityFilteredTransactions.length;
		if (filterType === "overdue") {
			return entityFilteredTransactions.filter((t) => t.status === "Overdue")
				.length;
		}
		if (filterType === "uncategorized") {
			return entityFilteredTransactions.filter(
				(t) => t.category === "Uncategorised",
			).length;
		}
		if (filterType === "thisWeek") {
			const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
			return entityFilteredTransactions.filter((t) => t.date >= weekAgo).length;
		}
		return 0;
	};

	// ── Pre-filter for quick filter chips ───────────────────────────────────
	const quickFilteredTransactions =
		mode === "live"
			? entityFilteredTransactions
			: entityFilteredTransactions.filter((txn) => {
					if (filter === "overdue") return txn.status === "Overdue";
					if (filter === "uncategorized")
						return txn.category === "Uncategorised";
					if (filter === "thisWeek") {
						const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
						return txn.date >= weekAgo;
					}
					return true;
				});

	const handleFilterChange = useCallback(
		(newFilter: FilterType) => {
			setFilter(newFilter);
			if (mode === "live") {
				setServerPage(1);
				setPageCursors({});
			}
		},
		[mode],
	);

	// ── Column configuration ─────────────────────────────────────────────────
	const columns: ColumnConfig<TransactionDisplay>[] = [
		{
			key: "source",
			label: "Source",
			sortable: true,
			filterable: true,
			filterConfig: {
				type: "select",
				options:
					mode === "live" && sourceOptions.length > 0
						? sourceOptions
						: [
								"Sales Ledger",
								"Purchase Ledger",
								"Bank",
								"Sales Order",
								"Purchase Order",
							],
			},
			align: "left",
			width: 140,
			render: (row) => <span className="text-sm">{row.source ?? "—"}</span>,
		},
		{
			key: "dataSourceName",
			label: "Data Source",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 150,
			render: (row) => (
				<span className="text-sm text-muted-foreground">
					{row.dataSourceName ?? "—"}
				</span>
			),
		},
		{
			key: "reference",
			label: "Reference",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 130,
			render: (row) => (
				<span className="text-sm">{row.reference ?? "—"}</span>
			),
		},
		{
			key: "accountName",
			label: "Account Name",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 160,
			render: (row) => (
				<span className="text-sm">{row.accountName ?? "—"}</span>
			),
		},
		{
			key: "description",
			label: "Description",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 240,
			render: (row) => (
				<div className="truncate text-sm">{row.description}</div>
			),
		},
		{
			key: "transactionType",
			label: "Transaction Type",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			align: "left",
			width: 140,
			render: (row) => (
				<span className="text-sm">{row.transactionType ?? "—"}</span>
			),
		},
		{
			key: "category",
			label: "Category",
			sortable: true,
			filterable: true,
			filterConfig: {
				type: "select",
				options:
					mode === "live" && categoryOptions.length > 0
						? categoryOptions
						: [
								"Customer Receipts",
								"Other Income",
								"Materials",
								"Payroll",
								"Subcontractors",
								"Rent",
								"Equipment",
								"Uncategorised",
							],
			},
			align: "left",
			width: 150,
			render: (row) => (
				<span
					className={
						row.category === "Uncategorised"
							? "text-destructive text-sm"
							: "text-sm"
					}
				>
					{row.category}
				</span>
			),
		},
		{
			key: "date",
			label: "Transaction Date",
			sortable: true,
			filterable: true,
			filterConfig: { type: "date-range" },
			align: "left",
			width: 140,
			render: (row) => (
				<div className="flex items-center gap-1 text-sm">
					<Calendar className="h-3 w-3 text-muted-foreground" />
					{formatDate(row.date)}
				</div>
			),
		},
		{
			key: "dueDate",
			label: "Due Date",
			sortable: true,
			filterable: true,
			filterConfig: { type: "date-range" },
			align: "left",
			width: 130,
			render: (row) =>
				row.dueDate ? (
					<div
						className={cn(
							"flex items-center gap-1 text-sm",
							row.status === "Overdue" && "text-destructive font-semibold",
						)}
					>
						<Calendar className="h-3 w-3" />
						{formatDate(row.dueDate)}
					</div>
				) : (
					<span className="text-muted-foreground">—</span>
				),
		},
		{
			key: "amount",
			label: "Amount",
			sortable: true,
			filterable: true,
			filterConfig: { type: "number" },
			align: "right",
			width: 130,
			render: (row) => (
				<span
					className={cn(
						"font-semibold",
						row.grossValueIn > 0 ? "text-success-600" : "text-destructive",
					)}
				>
					{row.grossValueIn > 0 ? "+" : "-"}
					{formatCurrency(
						row.grossValueIn > 0 ? row.grossValueIn : row.grossValueOut,
						"full",
					)}
				</span>
			),
		},
		{
			key: "outstanding",
			label: "Outstanding",
			sortable: true,
			filterable: true,
			filterConfig: { type: "number" },
			align: "right",
			width: 120,
			render: (row) =>
				row.outstanding != null ? (
					<span className="text-sm font-semibold">
						{formatCurrency(row.outstanding, "full")}
					</span>
				) : (
					<span className="text-muted-foreground">—</span>
				),
		},
		{
			key: "status",
			label: "Status",
			sortable: true,
			filterable: true,
			filterConfig: {
				type: "select",
				options:
					mode === "live" && statusOptions.length > 0
						? statusOptions
						: ["Outstanding", "Overdue", "Settled", "Unknown"],
			},
			align: "left",
			width: 120,
			render: (row) => {
				const variant =
					row.status === "Settled"
						? "success"
						: row.status === "Outstanding"
							? "warning"
							: row.status === "Overdue"
								? "destructive"
								: row.status === "Unknown"
									? "secondary"
									: "info";
				return <Badge variant={variant}>{row.status}</Badge>;
			},
		},
		{
			key: "id",
			label: "Actions",
			sortable: false,
			filterable: false,
			align: "center",
			width: 80,
			render: (row) => (
				<Button
					size="sm"
					variant="ghost"
					onClick={(e) => {
						e.stopPropagation();
						setSelectedTransaction(row);
						setShowTransactionModal(true);
					}}
				>
					<Edit className="h-4 w-4" />
				</Button>
			),
		},
	];

	// ── Render ───────────────────────────────────────────────────────────────
	return (
		<div className="flex flex-col h-full space-y-2 px-2 pt-0 pb-2 overflow-y-auto">
			{/* Scope guidance banner */}
			<div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
				<button
					type="button"
					className="flex w-full items-start gap-3 text-left"
					onClick={() => setBannerExpanded((prev) => !prev)}
				>
					<Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
					<p className="flex-1 font-medium">
						These are transactions synced from your active data sources.
					</p>
					<ChevronDown
						className={cn(
							"mt-0.5 h-4 w-4 shrink-0 text-blue-500 transition-transform",
							bannerExpanded && "rotate-180",
						)}
					/>
				</button>
				{bannerExpanded && (
					<p className="mt-2 pl-7 text-blue-800 dark:text-blue-300">
						This table displays all transactions that have been automatically
						imported from your connected data sources. Recurring transactions
						created manually via the Recurring Transactions page are not
						included here — those are managed separately and feed directly into
						your forecast.
					</p>
				)}
			</div>

			{/* Account filter banner */}
			{activeAccountFilter && (
				<div className="flex items-center gap-3 px-4 py-3 bg-primary/10 border border-primary/20 rounded-lg">
					<Landmark className="h-4 w-4 text-primary shrink-0" />
					<div className="flex-1 text-sm">
						<span className="font-medium">Filtered by account: </span>
						<span className="text-muted-foreground">{activeAccountFilter}</span>
					</div>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
						onClick={() => setActiveAccountFilter(undefined)}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			)}

			{/* Error state (live mode only) */}
			{error && mode === "live" && (
				<div className="flex items-center justify-center py-12 text-destructive">
					{error}
				</div>
			)}

			{/* Maximus Table */}
			{!error && (
				<div className="min-h-[400px] flex-1 flex flex-col -mt-3">
					<MaximusTableReusable
						data={quickFilteredTransactions}
						columns={columns}
						title=""
						isLoading={isLoading && mode === "live"}
						searchPlaceholder="Search by description or category..."
						enableRowSelection={false}
						enableZebraStriping={true}
						initialVisibleColumns={savedVisibleColumns}
						onExportToExcel={mode === "live" && hasPermission("export_data") ? triggerExport : undefined}
						onColumnConfigSave={(visibleKeys) => {
							const cols = visibleKeys.map((key, idx) => ({
								key,
								isVisible: true,
								order: idx,
							}));
							const allColumnKeys = columns.map((c) => c.key);
							const hiddenCols = allColumnKeys
								.filter((k) => !visibleKeys.includes(k))
								.map((key, idx) => ({
									key,
									isVisible: false,
									order: visibleKeys.length + idx,
								}));
							saveColumnPreferences([...cols, ...hiddenCols]);
						}}
						onSortChange={mode === "live" ? handleSortChange : undefined}
						initialColumnFilters={tableInitialColumnFilters}
						onFilterChange={
							mode === "live" ? handleColumnFilterChange : undefined
						}
						pagination={{
							enabled: true,
							defaultPageSize: 25,
							pageSizeOptions: [25, 50, 100, 200],
							...(mode === "live"
								? {
										totalItems: serverTotalCount,
										controlledPage: serverPage,
										onPageChange: (page: number) => {
											setServerPage(page);
										},
										onPageSizeChange: (newSize: number) => {
											setServerPageSize(newSize);
											setServerPage(1);
											setPageCursors({});
										},
									}
								: {}),
						}}
						onSearchChange={mode === "live" ? handleSearchChange : undefined}
						onSearch={mode !== "live" ? (data, query) => {
							const q = query.toLowerCase();
							return data.filter(
								(txn) =>
									txn.description.toLowerCase().includes(q) ||
									txn.category.toLowerCase().includes(q),
							);
						} : undefined}
						rowIdAccessor={(row) => String(row.id)}
						headerActions={
							<>
								<Button
									variant={filter === "all" ? "default" : "outline"}
									onClick={() => handleFilterChange("all")}
									size="sm"
								>
									All ({getFilterCount("all")})
								</Button>
								<Button
									variant={filter === "thisWeek" ? "default" : "outline"}
									onClick={() => handleFilterChange("thisWeek")}
									size="sm"
								>
									This Week ({getFilterCount("thisWeek")})
								</Button>
								<Button
									variant={filter === "uncategorized" ? "default" : "outline"}
									onClick={() => handleFilterChange("uncategorized")}
									size="sm"
								>
									Uncategorised ({getFilterCount("uncategorized")})
								</Button>
								{auditMode && (
									<Popover>
										<PopoverTrigger asChild>
											<Button variant="ghost" size="sm" className="h-6 w-6 p-0">
												<Info className="h-4 w-4 text-blue-500" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-80" align="end">
											<div className="space-y-3">
												<div className="font-semibold text-sm border-b pb-2">
													🔍 Audit: Transaction Count Breakdown
												</div>
												<div className="space-y-2 text-sm">
													<div className="flex justify-between">
														<span className="text-muted-foreground">
															Total in system:
														</span>
														<span className="font-mono font-semibold">
															{allTransactions.length}
														</span>
													</div>
													<div className="flex justify-between">
														<span className="text-muted-foreground">
															After entity filter:
														</span>
														<span className="font-mono font-semibold">
															{entityFilteredTransactions.length}
														</span>
													</div>
													<div className="bg-blue-50 dark:bg-blue-950 p-2 rounded text-xs">
														<div className="font-semibold mb-1">
															Entity Filter Applied:
														</div>
														<div className="text-muted-foreground">
															Showing only transactions for:{" "}
															<span className="font-semibold text-foreground">
																Birmingham
															</span>
														</div>
													</div>
													<div className="flex justify-between pt-2 border-t">
														<span className="text-muted-foreground">
															After quick filter:
														</span>
														<span className="font-mono font-semibold text-blue-600">
															{quickFilteredTransactions.length}
														</span>
													</div>
												</div>
												<div className="text-xs text-muted-foreground pt-2 border-t">
													✓ All numbers verified from source data
												</div>
											</div>
										</PopoverContent>
									</Popover>
								)}
							</>
						}
					/>
				</div>
			)}

			<div className="flex justify-end">
				<FeatureFeedback feature="Transactions" />
			</div>

			<TransactionDetailModal
				open={showTransactionModal}
				onClose={() => {
					setShowTransactionModal(false);
					setSelectedTransaction(null);
				}}
				transaction={selectedTransaction as any}
				onSave={(updatedTransaction) => {
					if (
						selectedTransaction &&
						updatedTransaction.category !== selectedTransaction.category
					) {
						handleCategorySave(
							selectedTransaction,
							updatedTransaction.category as string,
						);
					}
				}}
			/>
		</div>
	);
};
